import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const UnauthorizedPage: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Get the reason and message from query params
  const searchParams = new URLSearchParams(location.search);
  const reason = searchParams.get('reason');
  const customMessage = searchParams.get('message');
  
  // Determine the content based on the reason
  const isSessionExpired = reason === 'session_expired';
  const isUnauthorized = reason === 'unauthorized';

  // Show browser notification if session expired
  useEffect(() => {
    if (isSessionExpired && 'Notification' in window) {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('Session Expired', {
            body: 'Your session has expired. Please login again.',
            icon: '/favicon.ico'
          });
        }
      });
    }
  }, [isSessionExpired]);
  
  return (
    <div className="unauthorized-page">
      <div className="unauthorized-container">
        <div className="unauthorized-content">
          <div className="unauthorized-icon">
            {isSessionExpired ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <polyline points="12 6 12 12 16 14"/>
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            )}
          </div>
          
          <h1>
            {isSessionExpired ? 'Session Expired' : isUnauthorized ? 'Access Denied' : 'Authentication Required'}
          </h1>
          <p className="main-message">
            {customMessage ? (
              decodeURIComponent(customMessage)
            ) : isSessionExpired ? (
              'Your session has expired. For security reasons, you have been logged out.'
            ) : isUnauthorized ? (
              user ? (
                <>Sorry {user?.name}, you don't have permission to access this page.</>
              ) : (
                'You do not have permission to access this resource.'
              )
            ) : (
              'Please log in to continue.'
            )}
          </p>

          {isSessionExpired && (
            <div className="session-info-box">
              <p><strong>What happened?</strong></p>
              <p>Your session was no longer valid. This can happen when:</p>
              <ul>
                <li>You've been inactive for an extended period</li>
                <li>You logged in from another device or browser</li>
                <li>The server was restarted</li>
              </ul>
              <p>Simply log in again to continue where you left off.</p>
            </div>
          )}
          
          <div className="unauthorized-actions">
            <Link to={isSessionExpired ? "/login" : "/"} className="btn btn-primary">
              {isSessionExpired ? 'Log In Again' : 'Go to Home'}
            </Link>
          </div>
          
          {!isSessionExpired && (
            <div className="contact-info">
              <p>
                If you believe you should have access, please contact the system administrator.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UnauthorizedPage; 